/**
 * Organizational charts from an indented list.
 *
 * Each line is one person or unit, `Name | Title`; a line indented under another reports
 * to it. Lines that report to nobody are separate roots, drawn side by side. The layout
 * is the plain top-down tree: every subtree takes a column as wide as its children need,
 * the parent sits over the middle of its children, and every level is one row — so boxes
 * can never overlap. With `compact`, a unit whose reports are all single boxes stacks them
 * in a hanging column under it instead of spreading them across the page, which is how
 * most org charts keep a wide team narrow.
 */

import { LINE_HEIGHT, type Rect } from "@/lib/tool-kit/charts/layout";
import {
  maxTextWidth,
  type TextStyle,
  wrapText,
} from "@/lib/tool-kit/charts/text";
import { stripBullet } from "@/lib/tool-kit/diagrams/mind-map";

/* ------------------------------------------------------------------- parse */

export interface OrgNode {
  id: number;
  /** The words before the first `|`. */
  name: string;
  /** Everything after it, one entry per `|`. */
  details: string[];
  depth: number;
  parent: number | null;
  children: number[];
  line: number;
}

export interface OrgParse {
  nodes: OrgNode[];
  roots: number[];
  /** Lines left out because the chart reached its limit. */
  dropped: number;
}

export const ORG_LIMIT = 120;

const LINE_BREAK = /\r\n|\r|\n/;
const LEADING_SPACE = /^[ \t ]*/;
const TAB_WIDTH = 4;

export function indentOf(line: string): number {
  let columns = 0;
  for (const char of LEADING_SPACE.exec(line)?.[0] ?? "") {
    columns =
      char === "\t"
        ? (Math.floor(columns / TAB_WIDTH) + 1) * TAB_WIDTH
        : columns + 1;
  }
  return columns;
}

/** Read the list. Bullets and numbering are removed; blank lines are skipped. */
export function parseOrgChart(text: string, limit = ORG_LIMIT): OrgParse {
  const nodes: OrgNode[] = [];
  const roots: number[] = [];
  const stack: { id: number; indent: number }[] = [];
  let dropped = 0;
  for (const [index, raw] of text.split(LINE_BREAK).entries()) {
    const words = stripBullet(raw);
    if (words === "") {
      continue;
    }
    if (nodes.length >= limit) {
      dropped += 1;
      continue;
    }
    const indent = indentOf(raw);
    while (stack.length > 0 && (stack.at(-1)?.indent ?? 0) >= indent) {
      stack.pop();
    }
    const parts = words
      .split("|")
      .map((part) => part.trim())
      .filter((part, i) => i === 0 || part !== "");
    const parent = stack.at(-1)?.id ?? null;
    const node: OrgNode = {
      id: nodes.length,
      name: parts[0] ?? "",
      details: parts.slice(1),
      depth: parent === null ? 0 : nodes[parent].depth + 1,
      parent,
      children: [],
      line: index + 1,
    };
    nodes.push(node);
    if (parent === null) {
      roots.push(node.id);
    } else {
      nodes[parent].children.push(node.id);
    }
    stack.push({ id: node.id, indent });
  }
  return { nodes, roots, dropped };
}

/** The first-level ancestor's index among the root's children; −1 for a root. */
export function branchOf(parse: OrgParse, id: number): number {
  let node = parse.nodes[id];
  if (node.parent === null) {
    return -1;
  }
  while (node.depth > 1 && node.parent !== null) {
    node = parse.nodes[node.parent];
  }
  const root = node.parent === null ? null : parse.nodes[node.parent];
  return root ? root.children.indexOf(node.id) : -1;
}

/* ------------------------------------------------------------------ layout */

export const NAME_FONT = 13;
export const DETAIL_FONT = 11.5;
const NAME_STYLE: TextStyle = { fontSize: NAME_FONT, bold: true };
const DETAIL_STYLE: TextStyle = { fontSize: DETAIL_FONT };
const MARGIN = 16;
const SIBLING_GAP = 18;
const TREE_GAP = 40;
const LEVEL_GAP = 36;
const HANG_INDENT = 30;
const HANG_GAP = 12;
const PAD_X = 10;
const PAD_Y = 8;
const MIN_WIDTH = 96;
const MAX_WIDTH = 190;
const WRAP = MAX_WIDTH - PAD_X * 2;

export interface PlacedOrgNode {
  node: OrgNode;
  rect: Rect;
  nameLines: string[];
  detailLines: string[];
}

export interface OrgChartLayout {
  width: number;
  height: number;
  nodes: PlacedOrgNode[];
  /** Connector polylines, each a list of `[x, y]` points. */
  connectors: [number, number][][];
}

interface Sized {
  nameLines: string[];
  detailLines: string[];
  height: number;
  width: number;
}

function sizeNode(node: OrgNode): Sized {
  const nameLines = wrapText(node.name || " ", WRAP, NAME_STYLE, 2);
  const detailLines = node.details.flatMap((detail) =>
    wrapText(detail, WRAP, DETAIL_STYLE, 2)
  );
  const textHeight =
    nameLines.length * NAME_FONT * LINE_HEIGHT +
    detailLines.length * DETAIL_FONT * LINE_HEIGHT;
  const widest = Math.max(
    maxTextWidth(nameLines, NAME_STYLE),
    maxTextWidth(detailLines, DETAIL_STYLE)
  );
  return {
    nameLines,
    detailLines,
    height: Math.ceil(textHeight + PAD_Y * 2),
    width: Math.ceil(widest + PAD_X * 2),
  };
}

function isHanging(parse: OrgParse, node: OrgNode, compact: boolean): boolean {
  return (
    compact &&
    node.depth >= 1 &&
    node.children.length >= 2 &&
    node.children.every((child) => parse.nodes[child].children.length === 0)
  );
}

/** Lay the chart out, boxes of one common width and one common height per level. */
export function layoutOrgChart(
  parse: OrgParse,
  compact: boolean
): OrgChartLayout {
  const sizes = parse.nodes.map(sizeNode);
  const boxWidth = Math.min(
    MAX_WIDTH,
    Math.max(MIN_WIDTH, ...sizes.map((size) => size.width))
  );
  const levels = Math.max(0, ...parse.nodes.map((node) => node.depth)) + 1;
  const levelHeight = Array.from({ length: levels }, () => 0);
  for (const node of parse.nodes) {
    levelHeight[node.depth] = Math.max(
      levelHeight[node.depth],
      sizes[node.id].height
    );
  }
  const rowTop: number[] = [];
  let cursor = MARGIN;
  for (const height of levelHeight) {
    rowTop.push(cursor);
    cursor += height + LEVEL_GAP;
  }

  const spanCache = new Map<number, number>();
  const spanOf = (id: number): number => {
    const known = spanCache.get(id);
    if (known !== undefined) {
      return known;
    }
    const node = parse.nodes[id];
    let span = boxWidth;
    if (isHanging(parse, node, compact)) {
      span = boxWidth + HANG_INDENT;
    } else if (node.children.length > 0) {
      const kids = node.children.map(spanOf);
      span = Math.max(
        boxWidth,
        kids.reduce((sum, kid) => sum + kid, 0) +
          SIBLING_GAP * (kids.length - 1)
      );
    }
    spanCache.set(id, span);
    return span;
  };

  const placed = new Map<number, Rect>();
  const connectors: [number, number][][] = [];
  let bottom = 0;
  const put = (id: number, x: number, y: number) => {
    const rect: Rect = {
      x,
      y,
      width: boxWidth,
      height: levelHeight[parse.nodes[id].depth],
    };
    placed.set(id, rect);
    bottom = Math.max(bottom, y + rect.height);
    return rect;
  };
  const place = (id: number, left: number): void => {
    const node = parse.nodes[id];
    const span = spanOf(id);
    const y = rowTop[node.depth];
    if (isHanging(parse, node, compact)) {
      const box = put(id, left, y);
      const trunkX = left + HANG_INDENT / 2;
      let last = box.y + box.height;
      for (const [j, child] of node.children.entries()) {
        const top =
          rowTop[node.depth + 1] + j * (levelHeight[node.depth + 1] + HANG_GAP);
        const rect = put(child, left + HANG_INDENT, top);
        const middle = top + rect.height / 2;
        connectors.push([
          [trunkX, middle],
          [rect.x, middle],
        ]);
        last = middle;
      }
      connectors.push([
        [trunkX, box.y + box.height],
        [trunkX, last],
      ]);
      return;
    }
    if (node.children.length === 0) {
      put(id, left + (span - boxWidth) / 2, y);
      return;
    }
    const kids = node.children;
    const total =
      kids.reduce((sum, kid) => sum + spanOf(kid), 0) +
      SIBLING_GAP * (kids.length - 1);
    let x = left + (span - total) / 2;
    const centres: number[] = [];
    for (const kid of kids) {
      place(kid, x);
      const rect = placed.get(kid) as Rect;
      centres.push(rect.x + rect.width / 2);
      x += spanOf(kid) + SIBLING_GAP;
    }
    const centre = (centres[0] + (centres.at(-1) ?? centres[0])) / 2;
    const box = put(id, centre - boxWidth / 2, y);
    const busY = rowTop[node.depth + 1] - LEVEL_GAP / 2;
    connectors.push([
      [centre, box.y + box.height],
      [centre, busY],
    ]);
    if (centres.length > 1) {
      connectors.push([
        [centres[0], busY],
        [centres.at(-1) ?? centres[0], busY],
      ]);
    }
    for (const [i, kid] of kids.entries()) {
      const rect = placed.get(kid) as Rect;
      connectors.push([
        [centres[i], busY],
        [centres[i], rect.y],
      ]);
    }
  };

  let left = MARGIN;
  for (const root of parse.roots) {
    place(root, left);
    left += spanOf(root) + TREE_GAP;
  }
  const width = Math.max(0, left - TREE_GAP) + MARGIN;
  return {
    width: Math.ceil(width),
    height: Math.ceil(bottom + MARGIN),
    nodes: parse.nodes.map((node) => ({
      node,
      rect: placed.get(node.id) as Rect,
      nameLines: sizes[node.id].nameLines,
      detailLines: sizes[node.id].detailLines,
    })),
    connectors,
  };
}

/* ------------------------------------------------------- text alternative */

export interface OrgRow {
  level: number;
  name: string;
  details: string[];
  reportsTo: string;
}

export function orgRows(parse: OrgParse): OrgRow[] {
  const rows: OrgRow[] = [];
  const walk = (id: number) => {
    const node = parse.nodes[id];
    rows.push({
      level: node.depth + 1,
      name: node.name,
      details: node.details,
      reportsTo: node.parent === null ? "" : parse.nodes[node.parent].name,
    });
    for (const child of node.children) {
      walk(child);
    }
  };
  for (const root of parse.roots) {
    walk(root);
  }
  return rows;
}

/** The chart written back as the indented list it came from. */
export function orgOutline(parse: OrgParse): string {
  return orgRows(parse)
    .map(
      (row) =>
        `${"  ".repeat(row.level - 1)}${[row.name, ...row.details].join(" | ")}`
    )
    .join("\n");
}

export function orgCounts(parse: OrgParse): { people: number; levels: number } {
  return {
    people: parse.nodes.length,
    levels: Math.max(0, ...parse.nodes.map((node) => node.depth + 1)),
  };
}
